import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { xIdentityPortIsMock } from "../../lib/api";
import { devDoorsQuery } from "../../lib/queries";
import { Monogram } from "../ui/monogram";

/**
 * Every door on the local stack, unfiltered and uncapped (local stacks only).
 *
 * NOB IS NOT A DIRECTORY, and this is not the beginning of one. What it is: the
 * answer to a problem search cannot solve on a laptop — a developer running two
 * invented identities needs the door the other one just opened without having to
 * guess at its address, and search answers a query with at most ten rows.
 * Reading it out of psql is the only alternative.
 *
 * It renders NOTHING unless `/api/health` reports the identity port on its local
 * stand-in, which is the same gate the sign-in picker uses. Against a real
 * provider the route does not exist and neither does this. It lives at the foot
 * of the Search room because that is the room it is a development version of —
 * it used to be a popover hanging off the shell's header, where nobody looking
 * for a door would think to look.
 */
export function DevDoors({ onGo }: { onGo: (handle: string) => void }) {
  const [available, setAvailable] = useState(false);
  const { data: doors = [] } = useQuery({ ...devDoorsQuery(), enabled: available });

  useEffect(() => {
    let live = true;
    void xIdentityPortIsMock().then((mock) => {
      if (live) setAvailable(mock);
    });
    return () => {
      live = false;
    };
  }, []);

  if (!available) return null;

  return (
    <section className="mt-6 rounded-xl border border-border/80 p-4">
      <h3 className="text-xs uppercase tracking-[0.16em] text-faint">Development</h3>
      <p className="mt-2 text-xs leading-relaxed text-fainter">
        Every door on this stack, in one list. Nob is not a directory — in the product a door is
        reached by its address, from whoever owns it.
      </p>

      {doors.length === 0 ? (
        <p className="mt-4 text-sm text-faint">No doors on this stack yet.</p>
      ) : (
        <ul className="mt-3">
          {doors.map((door) => (
            <li key={door.handle}>
              <button
                type="button"
                onClick={() => onGo(door.handle)}
                className="nob-row flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left
                           hover:bg-hover focus-visible:outline-none focus-visible:ring-2
                           focus-visible:ring-focus/70"
              >
                <Monogram name={door.display_name} handle={door.handle} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-mono text-sm text-text-soft">
                    @{door.handle}
                  </span>
                  <span className="mt-0.5 block truncate font-mono text-xs tabular-nums text-fainter">
                    {door.has_house_rules ? "rules" : "no rules"} · {door.scored_knocks} scored
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
